import React, { useState, useEffect, useRef } from 'react';
import { buildFileURL } from 'utils/data';
import { EVENTS } from 'utils/constants';
import { getShowSpeakers } from 'utils/params';
import './index.scss';

const parseVoiceEvents = (xmlText) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, 'application/xml');
  const allEvents = Array.from(doc.querySelectorAll('event'));

  // The audio file starts capturing when the meeting room is created, so
  // MeetingCreatedEvent is the correct zero for player currentTime alignment.
  // Fall back to RecordStatusEvent if MeetingCreatedEvent is absent.
  const zeroEvent =
    allEvents.find(e => e.getAttribute('eventname') === 'MeetingCreatedEvent') ||
    allEvents.find(e =>
      e.getAttribute('eventname') === 'RecordStatusEvent' &&
      e.querySelector('status')?.textContent?.trim() === 'true'
    );

  if (!zeroEvent) {
    console.warn('[TalkingIndicator] No suitable zero event found in events.xml');
    return [];
  }

  const audioZeroMs = parseInt(zeroEvent.querySelector('timestampUTC')?.textContent, 10);
  if (!audioZeroMs) {
    console.warn('[TalkingIndicator] Could not parse zero event timestampUTC');
    return [];
  }

  console.log('[TalkingIndicator] Audio zero event:', zeroEvent.getAttribute('eventname'), audioZeroMs);

  // Build participant -> display name map from voice join events
  const nameMap = {};
  for (const event of allEvents) {
    if (event.getAttribute('eventname') === 'ParticipantJoinedEvent' && event.getAttribute('module') === 'VOICE') {
      const participant = event.querySelector('participant')?.textContent?.trim();
      const callerName = event.querySelector('callername')?.textContent?.trim();
      if (participant && callerName) nameMap[participant] = callerName;
    }
  }

  // Process ParticipantTalkingEvent entries (talking=true opens a segment, false closes it)
  const segments = [];
  const active = {};

  for (const event of allEvents) {
    if (event.getAttribute('eventname') !== 'ParticipantTalkingEvent') continue;

    const userId = event.querySelector('participant')?.textContent?.trim();
    const talking = event.querySelector('talking')?.textContent?.trim() === 'true';
    const tsMs = parseInt(event.querySelector('timestampUTC')?.textContent, 10);
    if (!userId || !tsMs) continue;

    const relSec = (tsMs - audioZeroMs) / 1000;

    if (talking) {
      active[userId] = { name: nameMap[userId] || userId, startSec: relSec };
    } else if (active[userId]) {
      segments.push({ userId, name: active[userId].name, startSec: active[userId].startSec, endSec: relSec });
      delete active[userId];
    }
  }

  // Close any still-open segments (still talking at recording end)
  Object.entries(active).forEach(([userId, { name, startSec }]) => {
    segments.push({ userId, name, startSec, endSec: Infinity });
  });

  console.log(`[TalkingIndicator] Parsed ${segments.length} speaking segment(s)`);
  return segments;
};

const hashColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 65%, 42%)`;
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
};

const TalkingIndicator = () => {
  const [segments, setSegments] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const colorMap = useRef({});
  const enabled = getShowSpeakers();

  useEffect(() => {
    if (!enabled) return;

    const url = buildFileURL('events.xml');
    fetch(url, { cache: 'no-store' })
      .then(res => {
        if (!res.ok) {
          console.warn(`[TalkingIndicator] events.xml fetch failed (${res.status}), indicator disabled`);
          return null;
        }
        return res.text();
      })
      .then(text => {
        if (!text) { setSegments([]); return; }
        setSegments(parseVoiceEvents(text));
      })
      .catch((err) => {
        console.warn('[TalkingIndicator] Could not load events.xml:', err);
        setSegments([]);
      });
  }, [enabled]);

  useEffect(() => {
    if (!segments) return;

    const handleTimeUpdate = (event) => {
      const time = event.detail.time;
      const current = segments.filter(s => time >= s.startSec && time < s.endSec);
      setSpeakers(prev => {
        if (prev.length === current.length && prev.every((p, i) => p.userId === current[i].userId)) return prev;
        return current;
      });
    };

    document.addEventListener(EVENTS.TIME_UPDATE, handleTimeUpdate);
    return () => document.removeEventListener(EVENTS.TIME_UPDATE, handleTimeUpdate);
  }, [segments]);

  if (!enabled || !segments || speakers.length === 0) return null;

  return (
    <div className="talking-indicator">
      {speakers.map(s => {
        if (!colorMap.current[s.userId]) colorMap.current[s.userId] = hashColor(s.userId);
        return (
          <div key={s.userId} className="talking-pill">
            <span className="talking-avatar" style={{ backgroundColor: colorMap.current[s.userId] }}>
              {getInitials(s.name)}
            </span>
            <span className="talking-name">{s.name}</span>
          </div>
        );
      })}
    </div>
  );
};

export default TalkingIndicator;
